import { useEffect, useRef, useState } from 'react';
import { $createParagraphNode, $createTextNode, $getSelection, $setSelection, $isRangeSelection, $getNearestNodeFromDOMNode, ElementNode, FORMAT_TEXT_COMMAND, $getRoot } from 'lexical';
import { $createQuoteNode, QuoteNode } from '@lexical/rich-text';
import { CodeNode, $createCodeNode } from '@lexical/code-core';
import { LinkNode, $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link';
import { ListNode, ListItemNode, INSERT_ORDERED_LIST_COMMAND, INSERT_UNORDERED_LIST_COMMAND, REMOVE_LIST_COMMAND } from '@lexical/list';
import { $setBlocksType } from '@lexical/selection';
import { $generateHtmlFromNodes, $generateNodesFromDOM } from '@lexical/html';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';

const protocols = new Set(['http:', 'https:', 'ftp:', 'ftps:', 'dat:', 'dweb:', 'ipfs:', 'ipns:', 'ssb:', 'gopher:', 'xmpp:', 'magnet:', 'gemini:']);
export function validComposerLink(value) {
  // Only prevent executable URL schemes when importing/rendering HTML. Do not
  // validate URL structure, hostnames or reachability in this playground.
  const scheme = /^([a-z][a-z0-9+.-]*:)/i.exec(value);
  return !!scheme && protocols.has(scheme[1].toLowerCase());
}

// Lexical's default URL allowlist is narrower than Mastodon's (e.g. xmpp).
// Replace only this composer's link node, keeping all URLs fail-closed.
export class ComposerLinkNode extends LinkNode {
  static getType() { return 'dr-composer-link'; }
  static clone(node) { return new ComposerLinkNode(node.__url, { target: node.__target, rel: node.__rel, title: node.__title }, node.__key); }
  static importJSON(json) { return new ComposerLinkNode(json.url, { target: json.target, rel: json.rel, title: json.title }).updateFromJSON(json); }
  static importDOM() { return { a: () => ({ conversion: node => ({ node: new ComposerLinkNode(node.getAttribute('href') ?? '') }), priority: 3 }) }; }
  exportJSON() { return { ...super.exportJSON(), type: 'dr-composer-link', version: 1 }; }
  sanitizeUrl(url) { return validComposerLink(url) ? url : 'about:blank'; }
}

// Ruby is the small pronunciation annotation supported by Mastodon's HTML
// sanitizer. Keep it editable, with normal text selection and safe DOM import.
function rubyClass(type, tag) {
  return class extends ElementNode {
    static getType() { return type; }
    static clone(node) { return new this(node.__key); }
    static importJSON(json) { return new this().updateFromJSON(json); }
    static importDOM() { return { [tag]: () => ({ conversion: () => ({ node: new this() }), priority: 2 }) }; }
    createDOM() { return document.createElement(tag); }
    updateDOM() { return false; }
    isInline() { return true; }
    exportJSON() { return { ...super.exportJSON(), type, version: 1 }; }
  };
}
export const RubyNode = rubyClass('dr-ruby', 'ruby');
export const RubyTextNode = rubyClass('dr-ruby-text', 'rt');
export const RubyFallbackNode = rubyClass('dr-ruby-fallback', 'rp');
export const richComposerNodes = [QuoteNode, CodeNode, ComposerLinkNode, { replace: LinkNode, with: node => new ComposerLinkNode(node.getURL(), { target: node.getTarget(), rel: node.getRel(), title: node.getTitle() }), withKlass: ComposerLinkNode }, ListNode, ListItemNode, RubyNode, RubyTextNode, RubyFallbackNode];

// A deliberately tiny HTML surface, not arbitrary Lexical styles. The server
// sanitizes again; client sanitizing also makes pasted HTML safe in the editor.
export function cleanComposerHtml(html) {
  const document = new DOMParser().parseFromString(html, 'text/html');
  const allowed = new Set(['P', 'BR', 'SPAN', 'A', 'DEL', 'S', 'PRE', 'BLOCKQUOTE', 'CODE', 'B', 'STRONG', 'U', 'I', 'EM', 'UL', 'OL', 'LI', 'RUBY', 'RT', 'RP']);
  document.body.querySelectorAll('script,style,iframe,svg,math,img,video,audio,object').forEach(node => node.remove());
  [...document.body.querySelectorAll('*')].reverse().forEach(node => {
    if (!allowed.has(node.tagName)) { node.replaceWith(...node.childNodes); return; }
    const href = node.tagName === 'A' ? node.getAttribute('href') : null;
    const start = node.tagName === 'OL' ? node.getAttribute('start') : null;
    const value = node.tagName === 'LI' ? node.getAttribute('value') : null;
    const reversed = node.tagName === 'OL' && node.hasAttribute('reversed');
    [...node.attributes].forEach(attribute => node.removeAttribute(attribute.name));
    if (href && validComposerLink(href)) node.setAttribute('href', href);
    else if (node.tagName === 'A') node.replaceWith(...node.childNodes);
    if (start && /^\d+$/.test(start)) node.setAttribute('start', start);
    if (value && /^\d+$/.test(value)) node.setAttribute('value', value);
    if (reversed) node.setAttribute('reversed', '');
    if (node.tagName === 'S') { const replacement = document.createElement('del'); replacement.append(...node.childNodes); node.replaceWith(replacement); }
  });
  return document.body.innerHTML;
}

export function $composerHtml(editor) { return cleanComposerHtml($generateHtmlFromNodes(editor)); }
function composerDocument(html) {
  const document = new DOMParser().parseFromString(cleanComposerHtml(html), 'text/html');
  // Lexical imports <s>, while Mastodon also accepts the semantic <del> tag.
  document.body.querySelectorAll('del').forEach(node => { const replacement = document.createElement('s'); replacement.append(...node.childNodes); node.replaceWith(replacement); });
  return document;
}
export function $setComposerHtml(editor, html) {
  const document = composerDocument(html);
  const nodes = $generateNodesFromDOM(editor, document);
  const root = $getRoot().clear();
  // Imported top-level inline nodes need a paragraph container.
  nodes.forEach(node => {
    if (node.getType() === 'text' || node.isInline?.()) {
      let paragraph = root.getLastChild();
      if (paragraph?.getType() !== 'paragraph') { paragraph = $createParagraphNode(); root.append(paragraph); }
      paragraph.append(node);
    } else root.append(node);
  });
  if (root.isEmpty()) root.append($createParagraphNode());
}
export function $insertComposerHtml(editor, html) {
  const document = composerDocument(html);
  const nodes = $generateNodesFromDOM(editor, document);
  const selection = $getSelection();
  if ($isRangeSelection(selection)) selection.insertNodes(nodes);
}

const labels = { bold: 'Bold', italic: 'Italic', underline: 'Underline', strikethrough: 'Strikethrough', code: 'Inline code' };
// Consistent outline icons, rather than a mixture of glyphs and word buttons.
const toolPaths = {
  link: 'M10 13a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-2 2 M14 11a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l2-2',
  bullet: 'M9 5h12 M9 12h12 M9 19h12 M3 5h.01 M3 12h.01 M3 19h.01',
  number: 'M10 6h11 M10 18h11 M3 3l1-1v8 M2 10h4 M2 16c0-3 4-3 4-1 0 2-4 3-4 6h4',
  quote: 'M4 3v18 M9 6h12 M9 12h8 M9 18h12',
  code: 'M8 7l-5 5 5 5 M16 7l5 5-5 5 M14 4l-4 16',
  block: 'M8 4L4 8l4 4 M16 4l4 4-4 4 M13 2l-2 12 M3 15v6h18v-6',
};
const toolIcon = name => <svg viewBox='0 0 24 24' aria-hidden='true' focusable='false' fill='none' stroke='currentColor' strokeWidth={name === 'more' ? 3 : 1.8} strokeLinecap='round' strokeLinejoin='round'><path d={toolPaths[name]} /></svg>;
export function FormattingToolbar({ disabled }) {
  const [editor] = useLexicalComposerContext();
  const [active, setActive] = useState({});
  const [dialog, setDialog] = useState(null), [url, setUrl] = useState(''), [linkText, setLinkText] = useState('');
  const [error, setError] = useState('');
  const [editingLink, setEditingLink] = useState(false);
  const canLink = url.length > 1 || (editingLink && !url.trim());
  const savedSelection = useRef(null);
  const modal = useRef(null);
  useEffect(() => {
    if (dialog && modal.current && !modal.current.open) modal.current.showModal();
  }, [dialog]);
  const closeDialog = () => { setDialog(null); editor.focus(); };
  useEffect(() => {
    const clickLink = event => {
      const anchor = event.target.closest?.('a');
      if (!anchor || disabled) return;
      event.preventDefault();
      editor.update(() => {
        let node = $getNearestNodeFromDOMNode(anchor);
        while (node && !$isLinkNode(node)) node = node.getParent();
        if (!$isLinkNode(node)) return;
        savedSelection.current = node.select(0, node.getChildrenSize()).clone();
        setLinkText(node.getTextContent());
        setUrl(node.getURL());
        setEditingLink(true);
        setError('');
        setDialog('link');
      }, { discrete:true });
    };
    return editor.registerRootListener((root, previous) => {
      previous?.removeEventListener('click', clickLink);
      root?.addEventListener('click', clickLink);
    });
  }, [editor, disabled]);
  useEffect(() => {
    const removeUpdate = editor.registerUpdateListener(({ editorState }) => editorState.read(() => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection)) return;
      const formats = Object.fromEntries(Object.keys(labels).map(format => [format, selection.hasFormat(format)]));
      let node = selection.anchor.getNode();
      while (node && node.getParent()?.getType() !== 'root') node = node.getParent();
      formats.block = node?.getType(); formats.list = node?.getListType?.();
      const anchor = selection.anchor.getNode();
      formats.link = $isLinkNode(anchor) || $isLinkNode(anchor.getParent());
      setActive(formats);
    }));
    return removeUpdate;
  }, [editor]);
  const action = callback => {
    if (disabled) return;
    // A fresh, unfocused editor has no selection. Establish its caret before
    // dispatching a formatting command, rather than losing that first click.
    editor.update(() => {
      if (!$isRangeSelection($getSelection())) $getRoot().selectEnd();
    }, { discrete: true });
    callback();
    editor.focus();
  };
  const block = (type, create) => action(() => editor.update(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) $setBlocksType(selection, active.block === type ? $createParagraphNode : create);
  }));
  const list = type => action(() => editor.dispatchCommand(active.list === type ? REMOVE_LIST_COMMAND : type === 'bullet' ? INSERT_UNORDERED_LIST_COMMAND : INSERT_ORDERED_LIST_COMMAND));
  const openDialog = type => {
    if (disabled) return;
    editor.update(() => {
      let selection = $getSelection() ?? $getRoot().selectEnd();
      let node = selection?.anchor?.getNode();
      if (!$isLinkNode(node)) node = node?.getParent();
      if ($isLinkNode(node)) selection = node.select(0, node.getChildrenSize());
      savedSelection.current = $isRangeSelection(selection) ? selection.clone() : null;
      setLinkText($isRangeSelection(selection) ? selection.getTextContent() : '');
      setUrl($isLinkNode(node) ? node.getURL() : '');
      setEditingLink($isLinkNode(node));
    }, { discrete: true });
    setError(''); setDialog(type);
  };
  const applyDialog = event => {
    event.preventDefault();
    event.stopPropagation();
    if (!canLink) return;
    if (!savedSelection.current) return;
    const input = url.trim();
    const target = /^[a-z][a-z0-9+.-]*:/i.test(input) ? input : `https://${input}`;
    const displayText = linkText || url;
    editor.update(() => {
      const selection = savedSelection.current;
      if (!selection) return;
      // Restore the selection lost when focus moved to the link/annotation field.
      $setSelection(selection);
        if (!input) {
          editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
          return;
        }
        if (selection.isCollapsed() || selection.getTextContent() !== displayText) {
          const text = $createTextNode(displayText).setFormat(selection.format);
          selection.insertNodes([text]);
          text.select(0, displayText.length);
        }
        editor.dispatchCommand(TOGGLE_LINK_COMMAND, target);
    });
    setDialog(null);
    editor.focus();
  };
  const button = (label, content, onClick, pressed = false, unavailable = false) => <button key={label} type='button' title={label} aria-label={label}
    aria-pressed={pressed} disabled={disabled || unavailable} onMouseDown={event => event.preventDefault()} onClick={onClick}>{content}</button>;
  return <>
    <div className='dr-formatting-toolbar' role='toolbar' aria-label='Post formatting'>
      <div className='dr-formatting-group' role='group' aria-label='Text style'>
        {Object.entries(labels).filter(([format]) => format !== 'code').map(([format, label]) => button(label, format === 'bold' ? <b>B</b> : format === 'italic' ? <i>I</i> : format === 'underline' ? <u>U</u> : <s>S</s>, () => action(() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, format)), active[format]))}
      </div>
      <div className='dr-formatting-group' role='group' aria-label='Links'>
        {button('Link', toolIcon('link'), () => openDialog('link'), active.link)}
      </div>
      <div className='dr-formatting-group' role='group' aria-label='Lists and quotes'>
        {button('Numbered list', toolIcon('number'), () => list('number'), active.list === 'number')}
        {button('Bulleted list', toolIcon('bullet'), () => list('bullet'), active.list === 'bullet')}
        {button('Blockquote', toolIcon('quote'), () => block('quote', $createQuoteNode), active.block === 'quote')}
      </div>
      <div className='dr-formatting-group' role='group' aria-label='Code'>
        {button('Inline code', toolIcon('code'), () => action(() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'code')), active.code)}
        {button('Code block', toolIcon('block'), () => block('code', () => $createCodeNode(null)), active.block === 'code')}
      </div>
    </div>
    {dialog && <dialog ref={modal} className='dr-link-modal' aria-label='Link' onCancel={event => { event.preventDefault(); closeDialog(); }} onPointerDown={event => {
          const bounds = event.currentTarget.getBoundingClientRect();
          if (event.target === event.currentTarget && (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom)) closeDialog();
        }} onKeyDown={event => {
          event.stopPropagation();
          if (event.key === 'Enter' && canLink) applyDialog(event);
          if (event.key === 'Escape') { event.preventDefault(); closeDialog(); }
        }}>
      <input id='link-url' aria-label='Link URL' autoFocus value={url} onChange={event => { setUrl(event.target.value); setError(''); }} placeholder='https://example.com' inputMode='url' autoComplete='off' spellCheck={false} />
      {error && <p role='alert'>{error}</p>}
      <footer><button type='button' className='dr-link-save' disabled={disabled || !canLink} onClick={applyDialog}>Link</button></footer>
    </dialog>}
  </>;
}
